import type { Currency } from "@/components/cost_index/types";
import type { Entity, Status } from "./api";

export { money, pctPoints } from "@/components/cost_index/format";

export const ENTITIES: Record<Entity, { name: string; short: string; currency: Currency; tz: string; location: string }> = {
  LTD: { name: "Ketosource Limited", short: "UK Ltd", currency: "GBP", tz: "Europe/London", location: "UK" },
  OU: { name: "Ketosource OÜ", short: "OÜ EU", currency: "EUR", tz: "Europe/Tallinn", location: "EU" },
  LLC: { name: "Ketosource Trading LLC", short: "LLC AE", currency: "AED", tz: "Asia/Dubai", location: "MENA" },
  US_LLC: { name: "Ketosource US LLC", short: "US LLC", currency: "USD", tz: "America/New_York", location: "US" },
};
export const PRODUCING_ENTITIES: Entity[] = ["LTD", "OU"];
export const RUN_ENTITIES: Entity[] = ["LTD", "OU", "LLC"];
export const PATH_B_ONLY: Entity[] = ["LLC"];
export const STATUSES: Status[] = ["Planned", "Draft", "Computed", "Awaiting Arnold", "Rejected", "Posted", "Cancelled"];
export const STATUS_LEGEND: Record<Exclude<Status, "Cancelled">, string> = {
  Planned: "Planned with the factory; not started. Edit, Start or Cancel it.",
  Draft: "Started. Declared SKUs, materials, pulls, documents and manual lines can change.",
  Computed: "COGS computed. Review the figures, then approve; any edit needs a new compute.",
  "Awaiting Arnold": "Bojan approved. Arnold reviews and posts, or rejects with a reason.",
  Rejected: "Arnold rejected it. Reopen to correct and recompute.",
  Posted: "Approved twice. The bill is final and the COGS records are written.",
};
export const STATUS_TONE: Record<Status, "muted" | "info" | "warn" | "ok" | "alert"> = {
  Planned: "muted", Draft: "info", Computed: "info", "Awaiting Arnold": "warn", Rejected: "alert", Posted: "ok", Cancelled: "muted",
};
export const ACCOUNT_NAMES: Record<string, string> = {
  "632": "Raw Material", "627.4": "Stock Freight - Raw Materials Inbound", "632.1": "Labour Costs",
  "627.2": "Stock Freight - Factory to First Warehouse", "627.1": "Stock Freight - International Trade",
  "628": "Customs", "629": "Cost of Goods", "630": "Inventory", "860": "Rounding",
};
export const SOURCE_ACCOUNTS: string[] = ["632.1", "627.2", "627.1", "628", "629", "627.4", "632"];
export const CURRENCIES: Currency[] = ["GBP", "EUR", "AED", "USD"];

export function qty(value: string | number | null | undefined, uom: "kg" | "unit" = "unit"): string {
  if (value === null || value === undefined || value === "") return "—";
  const formatted = new Intl.NumberFormat("en-GB", { maximumFractionDigits: uom === "kg" ? 3 : 0 }).format(Number(value));
  return uom === "kg" ? `${formatted} kg` : formatted;
}
export function pct(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value) * 100;
  return `${number > 0 ? "+" : ""}${number.toFixed(1)} %`;
}
export const COST_GROUPS: Record<string, string> = {
  "632": "Materials", "632.1": "Labour", "627.4": "Transport + customs", "627.2": "Transport + customs", "627.1": "Transport + customs",
  "628": "Transport + customs", "629": "Bought-in", "630": "Inventory", "860": "Rounding",
};
export function costGroup(line: { kind: string; account: string | null }): string {
  if (line.kind === "material" || line.account === null) return "Materials";
  return COST_GROUPS[line.account] ?? ACCOUNT_NAMES[line.account] ?? line.account;
}
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function shortDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value.length === 10 ? `${value}T12:00:00Z` : value);
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}
export function todayFor(entity: Entity, now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: ENTITIES[entity].tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}
export function accountLabel(code: string): string { return `${code} ${ACCOUNT_NAMES[code] ?? ""}`.trim(); }
export function skuSummary(skus: string[] | null, lineCount: number): string {
  if (!skus || skus.length === 0) return `${lineCount} SKUs`;
  return skus.length === 1 ? skus[0] : `${skus[0]} +${skus.length - 1}`;
}
